import React from "react";
import PropTypes from "prop-types";
import moment from "moment";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    MenuItem,
    Select,
    TextField
} from "@material-ui/core";
import "./SimpleDatePicker.css";
import EventIcon from "@material-ui/icons/Event";

const MIN_DATE = moment([1900, 0, 1]);
const MAX_DATE = moment([2100, 0, 1]);
const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

function toLocalDate(value) {
    if (value === null || value === undefined || value === "") return null;

    let parts;
    if (moment.isMoment(value)) {
        parts = [value.year(), value.month(), value.date()];
    } else if (value instanceof Date) {
        if (Number.isNaN(value.getTime())) return null;
        parts = [value.getFullYear(), value.getMonth(), value.getDate()];
    } else if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
        const parsed = moment(value.slice(0, 10), "YYYY-MM-DD", true);
        if (!parsed.isValid()) return null;
        parts = [parsed.year(), parsed.month(), parsed.date()];
    } else {
        const parsed = moment(value);
        if (!parsed.isValid()) return null;
        parts = [parsed.year(), parsed.month(), parsed.date()];
    }

    const date = moment(parts);
    return date.isValid() ? date : null;
}

function dateKey(date) {
    return date ? date.format("YYYY-MM-DD") : "";
}

function isAllowed(date) {
    return date && date.isSameOrAfter(MIN_DATE, "day") && date.isSameOrBefore(MAX_DATE, "day");
}

function boundedMonth(date) {
    const month = date.clone().startOf("month");
    if (month.isBefore(MIN_DATE, "month")) return MIN_DATE.clone().startOf("month");
    if (month.isAfter(MAX_DATE, "month")) return MAX_DATE.clone().startOf("month");
    return month;
}

class SimpleDatePicker extends React.Component {
    constructor(props) {
        super(props);
        const selected = toLocalDate(props.value);
        this.state = {
            open: false,
            viewDate: boundedMonth(selected || moment()),
            focusedDate: selected && isAllowed(selected) ? selected : boundedMonth(moment()),
            focusPending: false
        };
        this.fieldRef = React.createRef();
        this.dayRefs = {};
        this.returnFocusElement = null;
    }

    componentDidUpdate(prevProps) {
        const previousDate = toLocalDate(prevProps.value);
        const currentDate = toLocalDate(this.props.value);
        const valueChanged = dateKey(previousDate) !== dateKey(currentDate);
        const becameUnavailable =
            (!prevProps.disabled && this.props.disabled) ||
            (!prevProps.readOnly && this.props.readOnly);

        if (valueChanged || becameUnavailable) {
            const shouldClose = this.state.open && (valueChanged || becameUnavailable);
            this.setState({
                viewDate: boundedMonth(currentDate || moment()),
                focusedDate: currentDate && isAllowed(currentDate)
                    ? currentDate
                    : boundedMonth(moment()),
                open: shouldClose ? false : this.state.open,
                focusPending: false
            });
        } else if (this.state.focusPending) {
            const key = dateKey(this.state.focusedDate);
            const dayButton = this.dayRefs[key];
            if (dayButton) {
                this.setState({ focusPending: false });
                dayButton.focus();
            }
        }
    }

    handleOpen = () => {
        if (this.props.disabled || this.props.readOnly || this.state.open) return;
        const selected = toLocalDate(this.props.value);
        const viewDate = boundedMonth(selected || moment());
        const focusedDate = selected && isAllowed(selected) ? selected : viewDate.clone();
        this.returnFocusElement = document.activeElement;
        this.setState({
            open: true,
            viewDate,
            focusedDate,
            focusPending: false
        });
    };

    handleClose = () => {
        this.setState({ open: false, focusPending: false });
    };

    handleExited = () => {
        const target = this.fieldRef.current || this.returnFocusElement;
        if (target && typeof target.focus === "function") target.focus();
        this.returnFocusElement = null;
    };

    handleEntered = () => {
        const selected = toLocalDate(this.props.value);
        const initial = selected && isAllowed(selected)
            ? selected
            : this.state.viewDate.clone();
        const key = dateKey(initial);
        const button = this.dayRefs[key];
        if (button) button.focus();
    };

    setMonth = (month) => {
        const next = boundedMonth(this.state.viewDate.clone().month(month));
        this.updateView(next, null, false);
    };

    setYear = (year) => {
        const next = boundedMonth(this.state.viewDate.clone().year(Number(year)));
        this.updateView(next, null, false);
    };

    updateView = (viewDate, focusedDate, focusDay = true) => {
        const safeFocus = focusedDate && isAllowed(focusedDate)
            ? focusedDate.clone()
            : viewDate.clone();
        const boundedView = boundedMonth(viewDate);
        this.setState({
            viewDate: boundedView,
            focusedDate: safeFocus,
            focusPending: focusDay
        });
    };

    moveMonth = (amount) => {
        const next = boundedMonth(this.state.viewDate.clone().add(amount, "month"));
        const day = Math.min(this.state.focusedDate.date(), next.daysInMonth());
        const focus = next.clone().date(day);
        this.updateView(next, isAllowed(focus) ? focus : next);
    };

    moveFocus = (amount) => {
        const current = this.state.focusedDate || this.state.viewDate;
        const next = current.clone().add(amount, "day");
        if (!isAllowed(next)) return;
        this.updateView(next.clone().startOf("month"), next);
    };

    handleDayKeyDown = (event, date) => {
        const movements = {
            ArrowLeft: -1,
            ArrowRight: 1,
            ArrowUp: -7,
            ArrowDown: 7
        };
        if (movements[event.key] !== undefined) {
            event.preventDefault();
            this.moveFocus(movements[event.key]);
        } else if (event.key === "PageUp" || event.key === "PageDown") {
            event.preventDefault();
            const amount = event.key === "PageUp" ? -1 : 1;
            const next = date.clone().add(amount, "month");
            if (isAllowed(next)) this.updateView(next.clone().startOf("month"), next);
        } else if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            this.handleSelectDate(date);
        }
    };

    handleSelectDate = (date) => {
        if (this.props.disabled || this.props.readOnly || !isAllowed(date)) return;
        this.props.onChange(date.clone().hour(0).minute(0).second(0).millisecond(0));
        this.handleClose();
    };

    handleClear = () => {
        if (this.props.disabled || this.props.readOnly) return;
        this.props.onChange(null);
        this.handleClose();
    };

    renderDays() {
        const { viewDate, focusedDate } = this.state;
        const firstDay = viewDate.clone().startOf("month").day();
        const dayCount = viewDate.daysInMonth();
        const cells = [];

        for (let blank = 0; blank < firstDay; blank += 1) {
            cells.push(<span role="gridcell" aria-disabled="true" className="simple-date-picker__blank" key={`blank-${blank}`} />);
        }
        for (let day = 1; day <= dayCount; day += 1) {
            const date = viewDate.clone().date(day);
            const key = dateKey(date);
            const selected = dateKey(toLocalDate(this.props.value)) === key;
            const inRange = isAllowed(date);
            const isFocused = dateKey(focusedDate) === key;
            cells.push(
                <span role="gridcell" aria-selected={selected} key={key}>
                <button
                    type="button"
                    className={`simple-date-picker__day${selected ? " is-selected" : ""}${isFocused ? " is-focused" : ""}`}
                    ref={(node) => { this.dayRefs[key] = node; }}
                    aria-label={date.format("dddd, D MMMM YYYY")}
                    aria-pressed={selected}
                    tabIndex={isFocused ? 0 : -1}
                    disabled={!inRange}
                    onClick={() => this.handleSelectDate(date)}
                    onKeyDown={(event) => this.handleDayKeyDown(event, date)}
                >
                    {day}
                </button>
                </span>
            );
        }
        while (cells.length % 7) {
            cells.push(<span role="gridcell" aria-disabled="true" className="simple-date-picker__blank" key={`blank-${cells.length}`} />);
        }
        const rows = [];
        for (let index = 0; index < cells.length; index += 7) {
            rows.push(<div role="row" className="simple-date-picker__week" key={index}>{cells.slice(index, index + 7)}</div>);
        }
        return rows;
    }

    render() {
        const {
            id,
            value,
            label,
            fullWidth,
            margin,
            error,
            helperText,
            disabled,
            readOnly
        } = this.props;
        const selected = toLocalDate(value);
        const viewDate = this.state.viewDate;
        const minMonth = MIN_DATE.clone().startOf("month");
        const maxMonth = MAX_DATE.clone().startOf("month");
        const canGoPrevious = viewDate.isAfter(minMonth, "month");
        const canGoNext = viewDate.isBefore(maxMonth, "month");
        const titleId = `${id || "simple-date-picker"}-calendar-title`;
        const monthNames = moment.months();
        const years = [];
        for (let year = 1900; year <= 2100; year += 1) years.push(year);

        return (
            <React.Fragment>
                <TextField
                    id={id}
                    inputRef={this.fieldRef}
                    value={selected ? selected.format("DD-MM-YYYY") : ""}
                    label={label}
                    fullWidth={fullWidth}
                    margin={margin}
                    error={error}
                    helperText={helperText}
                    disabled={disabled}
                    onClick={this.handleOpen}
                    onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " " || event.key === "ArrowDown") {
                            event.preventDefault();
                            this.handleOpen();
                        }
                    }}
                    InputProps={{
                        readOnly: true,
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="Choose date of birth"
                                    disabled={disabled || readOnly}
                                    onClick={this.handleOpen}
                                    tabIndex={readOnly ? -1 : 0}
                                    className="simple-date-picker__open"
                                >
                                    <EventIcon />
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                    inputProps={{
                        "aria-label": label || "Choose date of birth",
                        "aria-haspopup": "dialog",
                        "aria-expanded": this.state.open,
                        readOnly: true
                    }}
                />
                <Dialog
                    open={this.state.open}
                    onClose={this.handleClose}
                    onEscapeKeyDown={this.handleClose}
                    fullWidth
                    maxWidth="xs"
                    aria-labelledby={titleId}
                    PaperProps={{
                        className: "simple-date-picker__paper",
                        style: { width: "calc(100% - 32px)", maxWidth: 352, margin: 16 }
                    }}
                    TransitionProps={{
                        onEntered: this.handleEntered,
                        onExited: this.handleExited
                    }}
                    className="simple-date-picker__dialog"
                >
                    <DialogTitle id={titleId} className="simple-date-picker__title">
                        Choose date of birth
                    </DialogTitle>
                    <DialogContent className="simple-date-picker__content">
                        <div className="simple-date-picker__month-bar">
                            <IconButton
                                aria-label="Previous month"
                                onClick={() => this.moveMonth(-1)}
                                disabled={!canGoPrevious}
                                className="simple-date-picker__month-arrow"
                            >
                                <span aria-hidden="true">‹</span>
                            </IconButton>
                            <div className="simple-date-picker__selectors">
                                <select
                                    aria-label="Calendar month"
                                    value={viewDate.month()}
                                    onChange={(event) => this.setMonth(Number(event.target.value))}
                                >
                                    {monthNames.map((month, index) => (
                                        <option value={index} key={month}
                                            disabled={viewDate.clone().month(index).isAfter(MAX_DATE, "month")}>
                                            {month}
                                        </option>
                                    ))}
                                </select>
                                <Select
                                    name="birthYear"
                                    value={viewDate.year()}
                                    onChange={(event) => this.setYear(event.target.value)}
                                    disableUnderline
                                    SelectDisplayProps={{ "aria-label": "Calendar year" }}
                                    MenuProps={{
                                        PaperProps: { style: { maxHeight: 200 } },
                                        MenuListProps: { "aria-label": "Birth years" }
                                    }}
                                    className="simple-date-picker__year"
                                >
                                    {years.map(year => <MenuItem key={year} value={year} dense>{year}</MenuItem>)}
                                </Select>
                            </div>
                            <IconButton
                                aria-label="Next month"
                                onClick={() => this.moveMonth(1)}
                                disabled={!canGoNext}
                                className="simple-date-picker__month-arrow"
                            >
                                <span aria-hidden="true">›</span>
                            </IconButton>
                        </div>
                        <div className="simple-date-picker__calendar" role="grid" aria-label={viewDate.format("MMMM YYYY")}>
                            <div role="row" className="simple-date-picker__week">
                            {WEEKDAYS.map((day) => (
                                <span className="simple-date-picker__weekday" role="columnheader" key={day}>
                                    {day}
                                </span>
                            ))}
                            </div>
                            {this.renderDays()}
                        </div>
                    </DialogContent>
                    <DialogActions className="simple-date-picker__actions">
                        <Button onClick={this.handleClear} disabled={disabled || readOnly} color="primary">
                            Clear
                        </Button>
                        <span className="simple-date-picker__action-spacer" />
                        <Button onClick={this.handleClose}>Cancel</Button>
                    </DialogActions>
                </Dialog>
            </React.Fragment>
        );
    }
}

SimpleDatePicker.propTypes = {
    id: PropTypes.string,
    value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.instanceOf(Date),
        PropTypes.object
    ]),
    onChange: PropTypes.func.isRequired,
    label: PropTypes.node,
    fullWidth: PropTypes.bool,
    margin: PropTypes.string,
    error: PropTypes.bool,
    helperText: PropTypes.node,
    disabled: PropTypes.bool,
    readOnly: PropTypes.bool
};

SimpleDatePicker.defaultProps = {
    id: "date-of-birth",
    value: null,
    label: "Date of Birth",
    fullWidth: false,
    margin: "dense",
    error: false,
    helperText: "",
    disabled: false,
    readOnly: false
};

export default SimpleDatePicker;
